/**
 * Argentina Province Administration Panel
 * Allows managing province activation for Argentina Bio Template
 */

import { useArgentinaBioTemplate } from "@/hooks/useArgentinaBioTemplate";
import { ARGENTINA_PROVINCES } from "@/lib/argentinaProvinceDetection";
import { useState } from "react";
import { MapPin, Users, ToggleLeft, ToggleRight, Download, Upload, RotateCcw, BarChart3, Eye, EyeOff } from "lucide-react";

interface ProvinceCardProps {
  province: {
    code: string;
    name: string;
    active: boolean;
    population: number;
    timezone: string;
    launchDate?: string;
  };
  onToggle: () => void;
}

function ProvinceCard({ province, onToggle }: ProvinceCardProps) {
  const formatPopulation = (pop: number) => {
    return new Intl.NumberFormat('es-AR').format(pop);
  };

  return (
    <div className={`border rounded-lg p-4 ui-transition duration-200 ${
      province.active
        ? 'border-green-500 bg-green-50 shadow-md'
        : 'border-gray-200 bg-gray-50'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${province.active ? 'bg-green-500' : 'bg-gray-400'}`} />
          <div>
            <h3 className="font-semibold text-gray-900">{province.name}</h3>
            <p className="text-sm text-gray-600">({province.code})</p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={`p-2 rounded-lg transition-colors ${
            province.active
              ? 'text-green-600 hover:bg-green-100'
              : 'text-gray-400 hover:bg-gray-100'
          }`}
          title={province.active ? 'Desactivar provincia' : 'Activar provincia'}
        >
          {province.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
        </button>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-gray-500" />
          <span className="text-gray-700">
            Población: {formatPopulation(province.population)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-gray-500" />
          <span className="text-gray-700 text-xs">
            {province.timezone}
          </span>
        </div>
        {province.launchDate && (
          <div className="text-xs text-green-600 font-medium">
            Activada: {new Date(province.launchDate).toLocaleDateString('es-AR')}
          </div>
        )}
      </div>
    </div>
  );
}

export function ArgentinaProvinceAdmin() {
  // Only show in development - NOT for production/public use
  if (!import.meta.env.DEV) {
    return null;
  }

  const {
    config,
    isLoading,
    error,
    isProvinceActive,
    getActiveProvinces,
    getInactiveProvinces,
    activateProvince,
    deactivateProvince,
    activateProvinces,
    deactivateProvinces,
    resetToDefaults,
    getProvinceStats,
    exportConfig,
    importConfig,
  } = useArgentinaBioTemplate();

  const [showInactive, setShowInactive] = useState(false);
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);

  // Only show in development
  if (!import.meta.env.DEV) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="fixed top-4 right-4 bg-blue-100 border border-blue-300 rounded-lg p-4 text-sm z-40">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span>Cargando configuración...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed top-4 right-4 bg-red-100 border border-red-300 rounded-lg p-4 text-sm z-40">
        <div className="text-red-800">Error: {error}</div>
      </div>
    );
  }

  const stats = getProvinceStats();
  const activeProvinces = getActiveProvinces();
  const inactiveProvinces = getInactiveProvinces();
  const displayedProvinces = showInactive ? config.provinces : activeProvinces;

  const handleExport = () => {
    const configJson = exportConfig();
    navigator.clipboard.writeText(configJson).then(() => {
      alert("Configuración copiada al portapapeles");
    });
  };

  const handleImport = () => {
    if (importText.trim()) {
      const success = importConfig(importText.trim());
      if (success) {
        alert("Configuración importada exitosamente");
        setImportText("");
        setShowImport(false);
      } else {
        alert("Error: Configuración inválida");
      }
    }
  };

  const handleActivateAll = () => {
    const inactiveCodes = inactiveProvinces.map(p => p.code);
    activateProvinces(inactiveCodes);
  };

  const handleDeactivateAll = () => {
    const activeCodes = activeProvinces.map(p => p.code);
    // Don't deactivate the default province
    const codesToDeactivate = activeCodes.filter(code => code !== config.defaultProvince);
    deactivateProvinces(codesToDeactivate);
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setShowInactive(!showInactive)}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-colors flex items-center gap-2"
      >
        <MapPin size={16} />
        Admin AR {showInactive ? '▼' : '▲'}
        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
          {activeProvinces.length}/{config.provinces.length}
        </span>
      </button>

      {/* Admin Panel */}
      {showInactive && (
        <div className="absolute top-12 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-96 max-h-[80vh] overflow-y-auto">
          <div className="mb-4">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              🇦🇷 Argentina Bio Template Admin
            </h3>

            {/* Statistics */}
            <div className="bg-blue-50 p-3 rounded mb-4">
              <div className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                <BarChart3 size={16} />
                Estadísticas
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-blue-600 font-medium">Provincias Activas</div>
                  <div className="text-lg font-bold text-blue-800">{stats.active}/{stats.total}</div>
                  <div className="text-blue-600">({stats.activePercentage}%)</div>
                </div>
                <div>
                  <div className="text-green-600 font-medium">Población Cubierta</div>
                  <div className="text-lg font-bold text-green-800">
                    {new Intl.NumberFormat('es-AR', { notation: 'compact' }).format(stats.activePopulation)}
                  </div>
                  <div className="text-green-600">({stats.activePopulationPercentage}%)</div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={handleActivateAll}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors flex items-center justify-center gap-1"
                >
                  <Eye size={14} />
                  Activar Todas
                </button>
                <button
                  onClick={handleDeactivateAll}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors flex items-center justify-center gap-1"
                >
                  <EyeOff size={14} />
                  Solo Default
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowImport(!showImport)}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm transition-colors flex items-center justify-center gap-1"
                >
                  <Upload size={14} />
                  {showImport ? 'Ocultar Import' : 'Importar'}
                </button>
                <button
                  onClick={handleExport}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors flex items-center justify-center gap-1"
                >
                  <Download size={14} />
                  Exportar
                </button>
              </div>
              <button
                onClick={resetToDefaults}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors flex items-center justify-center gap-1"
              >
                <RotateCcw size={14} />
                Reset a Default
              </button>
            </div>

            {/* Import Section */}
            {showImport && (
              <div className="mb-4">
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="Pega aquí la configuración JSON..."
                  className="w-full h-24 p-2 border rounded text-xs font-mono"
                />
                <button
                  onClick={handleImport}
                  className="mt-2 w-full bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  Importar Configuración
                </button>
              </div>
            )}

            {/* Province List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-700">
                  Provincias {showInactive ? 'Todas' : 'Activas'}
                </h4>
                <button
                  onClick={() => setShowInactive(!showInactive)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {showInactive ? 'Mostrar solo activas' : 'Mostrar todas'}
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {displayedProvinces.map((province) => (
                  <ProvinceCard
                    key={province.code}
                    province={province}
                    onToggle={() => {
                      if (province.active) {
                        deactivateProvince(province.code);
                      } else {
                        activateProvince(province.code);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-xs text-gray-500 border-t pt-2">
            <div>Provincia por defecto: {config.defaultProvince}</div>
            <div>Versión: {config.version}</div>
            <div>Última actualización: {new Date().toLocaleTimeString('es-AR')}</div>
          </div>
        </div>
      )}
    </div>
  );
}